<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { messageAlertState, type MessageAlertOptions } from '@/shared/ui/message-alert';
import { WarningFilled } from '@element-plus/icons-vue';

const value = ref('');
const submitting = ref(false);
// Holds the last-shown options so the button/icon theme stays put through the
// dialog's leave transition. Reading straight from messageAlertState (nulled on
// close) would drop `theme` mid-animation, flashing a danger button to primary.
const opts = ref<MessageAlertOptions>();
const open = computed({
  get: () => !!messageAlertState.value,
  set: (v) => {
    if (!v && !submitting.value) onCancel();
  },
});

watch(messageAlertState, (state) => {
  if (state) {
    value.value = '';
    submitting.value = false;
    opts.value = state.options;
  }
});
const showCancel = computed(() => opts.value?.type !== 'alert');
const showInput = computed(() => opts.value?.type === 'prompt');
const cancelText = computed(() => opts.value?.cancelText ?? 'Cancel');
const confirmText = computed(() => opts.value?.confirmText ?? 'Confirm');
const iconColor = computed(() =>
  opts.value?.theme === 'danger' ? 'text-danger' : 'text-brand-azure',
);
const canConfirm = computed(() => !opts.value?.checkInput || opts.value.checkInput === value.value);

async function onConfirm(): Promise<void> {
  const state = messageAlertState.value;
  if (!state || !canConfirm.value || submitting.value) return;
  const run = state.options.onConfirm;
  if (!run) {
    state.resolve(value.value);
    messageAlertState.value = null;
    return;
  }
  submitting.value = true;
  try {
    await run();
    state.resolve(value.value);
    messageAlertState.value = null;
  } catch {
    // Async confirm rejected: keep the dialog open so the user can retry.
    // Error surfacing is the caller's onConfirm (the axios interceptor toasts).
  } finally {
    submitting.value = false;
  }
}

function onCancel(): void {
  const state = messageAlertState.value;
  if (!state || submitting.value) return;
  state.reject(new Error('cancelled'));
  messageAlertState.value = null;
}
</script>

<template>
  <ElDialog
    v-model="open"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="!submitting"
    width="600px"
    class="sr-message-alert"
    data-testid="alert-dialog"
  >
    <template #header>
      <div class="text-fg flex items-center gap-2 text-base font-semibold">
        <ElIcon :size="18" :class="iconColor">
          <WarningFilled />
        </ElIcon>
        <span>{{ opts?.title }}</span>
      </div>
    </template>
    <div>
      <div v-if="opts?.content" class="text-fg whitespace-pre-line">{{ opts.content }}</div>
      <ElInput v-if="showInput" v-model="value" :placeholder="opts?.placeholder" class="mt-4" />
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <ElButton
          v-if="showCancel"
          text
          :disabled="submitting"
          data-testid="alert-cancel-btn"
          @click="onCancel"
        >
          {{ cancelText }}
        </ElButton>
        <ElButton
          :type="opts?.theme === 'danger' ? 'danger' : 'primary'"
          :loading="submitting"
          :disabled="!canConfirm || submitting"
          data-testid="alert-confirm-btn"
          @click="onConfirm"
        >
          {{ confirmText }}
        </ElButton>
      </div>
    </template>
  </ElDialog>
</template>

<style>
.sr-message-alert.el-dialog {
  padding: 0;
}
.sr-message-alert .el-dialog__header {
  padding: 30px 24px 16px;
  margin-right: 0;
}
.sr-message-alert .el-dialog__body {
  padding: 0 24px;
}
.sr-message-alert .el-dialog__footer {
  padding: 24px 24px 16px;
}
</style>
